<template>
  <section>
    <NotificationCenter
      v-if="isNotificationsShown"
      v-bind="{ errors, notifications, successes, warnings }"
    />
    <LoadingLayout v-if="isAppLoading" />
    <MainLayout v-else />
    <CaptchaBlock
      ref="invisibleRecaptcha"
      keyType="login"
      @captcha-verify-action="setCaptchaToken"
    />
  </section>
</template>

<script>
import { getAccessToken } from '@internetworkexpert/js-common'
import { mapState, mapMutations, mapActions } from 'vuex'
import LoadingLayout from '@/layouts/LoadingLayout.vue'
import MainLayout from '@/layouts/MainLayout.vue'
import NotificationCenter from '@/components/NotificationCenter.vue'
import CONSTANTS from '@/constants'
import router from '@/router'
import { eventsConstants } from '@/helpers/eventsList'
import CaptchaBlock from '@/components/shared/CaptchaBlock.vue'
import { updateCart } from '@/helpers/mixins'

export default {
  name: 'App',
  components: {
    LoadingLayout,
    MainLayout,
    CaptchaBlock,
    NotificationCenter,
  },
  mixins: [updateCart],
  data() {
    return {
      captchaToken: '',
    }
  },
  computed: {
    ...mapState({
      notifications: state => state.app.appNotifications,
      errors: state => state.app.appErrors,
      successes: state => state.app.appSuccesses,
      warnings: state => state.app.appWarnings,
      userProfile: state => state.auth.userProfile,
      isAppLoading: state => state.app.isAppLoading,
      isAuthenticated: state => state.auth.isAuthenticated,
      customCoupon: state => state.shoppingCart.coupon,
      availablePlans: state => state.products.availablePlans,
      isInPartnerFlow: state => state.subscriptions.isInPartnerFlow,
      recurlyTokenActionResult: state => state.subscriptions.recurlyTokenActionResult,
      paymentMethod: state => state.subscriptions.paymentMethod,
      checkoutTotal: state => state.subscriptions.checkoutTotal,
      utmParams: state => state.events.utmParams,
      promotionalField: state => state.subscriptions.promotionalField,
      shoppingCart: state => state.shoppingCart.shoppingCart,
      availableCertifications: state => state.products.availableCertifications,
      availableLiveVirtualTrainings: state => state.products.availableLiveVirtualTrainings,
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
      selectedProductType: state => state.products.selectedProductType,
    }),
    recurlyKey() {
      return process.env.VUE_APP_RECURLY_KEY
    },
    userHasSubscription() {
      return this.usersCurrentSubscriptions.length > 0
    },
    isNotificationsShown() {
      return (
        !!this.errors.length > 0 ||
        !!this.notifications.length > 0 ||
        !!this.successes.length > 0 ||
        !!this.warnings.length > 0
      )
    },
  },
  watch: {
    isInPartnerFlow(state) {
      if (state) {
        this.submitPartnerStackInfo()
      }
    },
  },
  methods: {
    ...mapMutations({
      updateSelectedProductType: 'products/SELECTED_PRODUCT_TYPE_UPDATED',
    }),
    ...mapActions({
      initializeDarkly: 'featureFlags/initializeDarkly',
    }),
    async fetchPlaceOrderRecaptcha() {
      await this.$refs.invisibleRecaptcha.execute()
    },
    setCaptchaToken({ recaptcha }) {
      this.captchaToken = recaptcha
    },
    async submitPartnerStackInfo() {
      try {
        if (growsumo) {
          const { first_name, last_name, email, id } = this.userProfile
          growsumo.data.name = `${first_name} ${last_name}`
          growsumo.data.email = email
          growsumo.data.customer_key = id
          growsumo.createSignup()
        }
      } catch (error) {
        this.$store.dispatch('app/setError', error)
      }
    },
    handleCouponParam() {
      const params = new URLSearchParams(window.location.search)
      const coupon = params.get('coupon')
      if (coupon && coupon.length) {
        this.$store.dispatch('shoppingCart/updateCoupon', coupon)
      }
    },
    handleParams() {
      const { PLANS } = CONSTANTS
      const params = new URLSearchParams(window.location.search)
      const products = params.get('products') || params.get('product_selected')
      const plan = params.get('plan')
      this.saveUTMVars(params)
      const promoParam = params.get('special_offer')
      if (promoParam && promoParam.length) {
        this.$store.dispatch('subscriptions/setPromotionalField', promoParam)
      }
      if (plan && plan.length) {
        const intendedPlan = this.availablePlans.find(p => p.merchant_id === plan)
        if (intendedPlan && !this.userHasSubscription) {
          this.$store.dispatch('shoppingCart/addProductToShoppingCart', intendedPlan)
        }
      }
      if (products && products.length) {
        const productsArray = products.split(',')
        productsArray.map(i => {
          const planIsBundle =
            i.trim() === PLANS.QUARTERLY_EJPT ||
            i.trim() === PLANS.EDA_PREMIUM ||
            i.trim() === PLANS.EWPT_PREMIUM
          const foundPlan = this.availablePlans.find(
            p => p.merchant_id === i.trim() && !planIsBundle
          )
          const foundBundle = this.availablePlans.find(
            p => p.merchant_id === i.trim() && planIsBundle
          )
          const foundCertification = this.availableCertifications.find(
            p => p.merchant_id === i.trim()
          )
          const foundLiveVirtualTraining = this.availableLiveVirtualTrainings.find(
            p => p.merchant_id === i.trim()
          )
          if (foundPlan) {
            if (!this.shoppingCart.includes(foundPlan) && !this.userHasSubscription) {
              this.updateSelectedProductType('Subscriptions')
              this.updateSelectedPlan(foundPlan)
              if (params.get('product_selected')) {
                if (this.isAuthenticated) {
                  router.push({ name: 'payment' })
                } else {
                  router.push({ name: 'account' })
                }
              }
            }
          }
          if (foundBundle) {
            if (!this.shoppingCart.includes(foundBundle) && !this.userHasSubscription) {
              this.updateSelectedProductType('Bundles')
              this.updateSelectedPlan(foundBundle)
              if (params.get('product_selected')) {
                if (this.isAuthenticated) {
                  router.push({ name: 'payment' })
                } else {
                  router.push({ name: 'account' })
                }
              }
            }
          }
          if (foundCertification) {
            if (!this.shoppingCart.includes(foundCertification)) {
              this.updateSelectedProductType('Certification Vouchers')
              this.updateSelectedPlan(foundCertification)
              if (params.get('product_selected')) {
                if (this.isAuthenticated) {
                  router.push({ name: 'payment' })
                } else {
                  router.push({ name: 'account' })
                }
              }
            }
          }
          if (foundLiveVirtualTraining) {
            if (!this.shoppingCart.includes(foundLiveVirtualTraining)) {
              this.$store.dispatch(
                'shoppingCart/addProductToShoppingCart',
                foundLiveVirtualTraining
              )
              // if user is logged in and has an specific plan, add the coupon to gett the discount
              if (this.isAuthenticated) {
                this.$store.dispatch(
                  'shoppingCart/updateCoupon',
                  CONSTANTS.LIVE_VIRTUAL_TRAININGS.COUPONS[foundLiveVirtualTraining.merchant_id]
                )
                router.push({ name: 'payment' })
              } else {
                router.push({ name: 'account' })
              }
            }
          }
        })
      }
    },
    saveUTMVars(params) {
      const payload = {}
      Object.keys(CONSTANTS.UTM_VARS).map(key => {
        payload[key] = params.get(key)
      })

      this.$store.commit('events/UPDATE_UTM_PARAMS', payload)
    },
    sendCheckoutStartedEvent() {
      this.sendMixpanelEvent(eventsConstants.CHECKOUT_STARTED, {
        coupon: this.customCoupon,
        available_plans: `${this.availablePlans.map(p => p.plan_code)}`,
        special_offer: this.promotionalField,
        ...this.utmParams,
      })
    },
  },
  async created() {
    this.$store.dispatch('app/isAppLoading', true)
    try {
      const token = getAccessToken()
      if (token && token.length) {
        await this.$store.dispatch('auth/attemptLogin', token)
      }
      if (this.isAuthenticated) {
        await this.$store.dispatch('subscriptions/getCurrentPaymentMethodOnFile')
        await this.$store.dispatch('business/getBusinessUser')
      }
      this.handleCouponParam()
      await this.$store.dispatch('products/fetchAvailableProducts')
      await this.$store.dispatch('products/getPassTypes')
      this.handleParams()
      await this.initializeDarkly()
      if (this.isAuthenticated) {
        this.sendCheckoutStartedEvent()
      }
    } catch (error) {
      this.$store.dispatch('app/setError', error)
    }
    this.$store.dispatch('app/isAppLoading', false)
  },
  mounted() {
    window.recurly.configure(this.recurlyKey)
  },
}
</script>

<style lang="scss">
// Account for notches and statusbars
body {
  padding-top: constant(safe-area-inset-top);
  padding-top: env(safe-area-inset-top);
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: constant(safe-area-inset-left);
  padding-left: env(safe-area-inset-left);
  padding-right: constant(safe-area-inset-right);
  padding-right: env(safe-area-inset-right);
  scroll-behavior: smooth;
}
@import './src/styles/app.scss';
@import '~vue-loaders/dist/vue-loaders.css';
$material-icons-font-path: '~material-icons/iconfont/';
@import '~material-icons/iconfont/material-icons.scss';

html {
  scroll-behavior: smooth;
}

* {
  -webkit-font-smoothing: antialiased;
}

.container {
  max-width: 100%;
  min-height: 150vh;
  background: var(--main-bg-color);
  background-image: url('./assets/images/backgrounds/main_bg.jpg');
  background-position: inherit;
  background-repeat: no-repeat;
  background-size: cover;
  margin: 0 auto;
  padding: 0 0 50px 0;
  @include tablet {
    min-height: 100vh;
  }
  &__mask {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.8);
  }
}
#recurly-3ds {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 98%;
  width: 98%;
  margin: auto;
  z-index: 9999;
}
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.2s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
